import { useState, Suspense } from "react";
import { ErrorBoundary } from "@suspensive/react";
import { SuspenseQuery } from "@suspensive/react-query";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SurveyStatsCards } from "@/components/survey/survey-stats-cards";
import { SurveyDistributionChart } from "@/components/survey/survey-distribution-chart";
import { SurveyCrossTabChart } from "@/components/survey/survey-cross-tab-chart";
import { SurveyResponseTable } from "@/components/survey/survey-response-table";
import {
  surveyStatsQueryOptions,
  surveyResponsesQueryOptions,
} from "@/hooks/use-survey";
import { TableLoadingFallback } from "@/components/suspense/loading-fallback";
import { SectionErrorFallback } from "@/components/error-boundary/error-fallback";

function StatsLoadingFallback() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i}>
          <CardContent className="pt-6">
            <div className="h-8 w-24 animate-pulse rounded bg-muted" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ChartLoadingFallback() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i}>
          <CardContent className="pt-6">
            <div className="h-[250px] animate-pulse rounded bg-muted" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function SurveyPage() {
  const [page, setPage] = useState(1);
  const limit = 20;

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          온보딩 설문 모니터링
        </h1>
        <p className="text-muted-foreground leading-normal">
          사용자 온보딩 설문 응답을 조회합니다.
        </p>
      </div>

      <QueryErrorResetBoundary>
        {({ reset }) => (
          <ErrorBoundary onReset={reset} fallback={SectionErrorFallback}>
            <Suspense
              fallback={
                <>
                  <StatsLoadingFallback />
                  <ChartLoadingFallback />
                </>
              }
            >
              <SuspenseQuery {...surveyStatsQueryOptions()}>
                {({ data: stats }) => (
                  <>
                    <SurveyStatsCards stats={stats} />

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <SurveyDistributionChart
                        title="작가 정체성"
                        data={stats.writerIdentityDistribution}
                        chartType="pie"
                      />
                      <SurveyDistributionChart
                        title="집필 형식"
                        data={stats.writingFormatDistribution}
                        chartType="pie"
                      />
                      <SurveyDistributionChart
                        title="장르 분포 (Top 20)"
                        data={stats.genreDistribution}
                        chartType="bar"
                      />
                      <SurveyDistributionChart
                        title="단계별 도달"
                        data={stats.stepFunnelDistribution}
                        chartType="bar"
                      />
                    </div>

                    <SurveyCrossTabChart
                      title="집필 형식 × 작가 정체성"
                      data={stats.writingFormatByIdentity}
                    />
                  </>
                )}
              </SuspenseQuery>
            </Suspense>
          </ErrorBoundary>
        )}
      </QueryErrorResetBoundary>

      <Card>
        <CardHeader>
          <CardTitle>개별 응답 목록</CardTitle>
        </CardHeader>
        <CardContent className="px-4 md:px-6">
          <QueryErrorResetBoundary>
            {({ reset }) => (
              <ErrorBoundary onReset={reset} fallback={SectionErrorFallback}>
                <Suspense
                  fallback={<TableLoadingFallback columnCount={8} />}
                >
                  <SuspenseQuery
                    {...surveyResponsesQueryOptions({ page, limit })}
                  >
                    {({ data }) => (
                      <SurveyResponseTable
                        data={data}
                        onPageChange={setPage}
                      />
                    )}
                  </SuspenseQuery>
                </Suspense>
              </ErrorBoundary>
            )}
          </QueryErrorResetBoundary>
        </CardContent>
      </Card>
    </div>
  );
}
